import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { CheckCircle2, Save, Settings as SettingsIcon } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import {
  useTenantSettings,
  useUpdateTenantSettings,
  useUploadLogo,
  type DepositBank,
  type TenantSettings,
} from './api'
import { PaymentGatewayCredentialsCard } from './PaymentGatewayCredentialsCard'
import { MockLmsCard } from './MockLmsCard'
import { useMe } from '@/features/auth/api'
import { resolveLogoUrl } from '@/lib/api/client'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import { useFreshness } from '@/features/freshness/api'

const DEFAULT_WORKING_DAYS = 'MON,TUE,WED,THU,FRI,SAT'
const DEFAULT_PRIMARY_COLOR = '#204887'

const HEX_COLOR_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/

// <input type="color"> only accepts 6-digit hex; expand a 3-digit shorthand.
function expandHex(hex: string) {
  return /^#[0-9A-Fa-f]{3}$/.test(hex)
    ? '#' + hex.slice(1).split('').map((c) => c + c).join('')
    : hex
}

/** What the server writes after a logo upload; round-trips on save. */
const UPLOADED_LOGO_RE = /^\/v1\/branding\/[a-z0-9-]+\/logo\?v=[0-9a-f]{12}$/

/** Mirrors the server's limits on receipt.terms (TenantSettingsService). */
const MAX_RECEIPT_TERMS = 8
const MAX_RECEIPT_TERM_LENGTH = 200

/**
 * One point per line. A leading "1." or "2)" is dropped, since the receipt
 * numbers the points itself and pasted terms usually come numbered.
 */
function parseReceiptTerms(text: string) {
  return text
    .split('\n')
    .map((l) => l.replace(/^\s*\d+\s*[.)]\s*/, '').trim())
    .filter(Boolean)
}

function isLikelyUrl(u: string) {
  if (!u.trim()) return true // empty = "no logo", allowed
  if (UPLOADED_LOGO_RE.test(u.trim())) return true
  try {
    const p = new URL(u.trim())
    return p.protocol === 'http:' || p.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Tenant settings — Conduct, Reconciliation, Security, Branding. Mounts
 * under /settings; gated by TENANT_CONFIG_MANAGE on the backend. Unknown
 * keys (reserved for future tunables) are preserved on the round-trip.
 */
export function SettingsPage() {
  const { data, isPending, isError } = useTenantSettings()
  const save = useUpdateTenantSettings()

  // Cosmetic, as everywhere else: the endpoint enforces the permission too.
  const { data: me } = useMe()
  const canManageGateway = navPermissionCheck(me)('PAYMENT_GATEWAY_CONFIG_MANAGE')
  const graviton = me?.tenantTier === 'GRAVITON'

  // Local form state; reset whenever the server payload changes (so save +
  // server-merge propagates cleanly).
  const [conduct, setConduct] = useState({
    contact_hours_start: '08:00',
    contact_hours_end: '19:30',
    contact_zone: 'Asia/Kolkata',
    frequency_cap_per_day: '3',
    contact_working_days: DEFAULT_WORKING_DAYS,
  })
  const [recon, setRecon] = useState({
    auto_action_confidence_threshold: '85',
    auto_action_kinds: 'EXTERNALLY_PAID',
  })
  const [security, setSecurity] = useState({
    manager_totp_required: true,
  })
  const [trips, setTrips] = useState({
    auto_approve_enabled: false,
    auto_approve_mode: 'ALL' as 'ALL' | 'ALLOWLIST',
    auto_approve_agent_emp_ids: '',
  })
  // '' is a real, chosen state, not a placeholder: it means the tenant has
  // not said which world it is in, and digital collections stay refused.
  const [payments, setPayments] = useState({
    mode: '' as '' | 'MOCK' | 'LIVE',
    link_expiry_minutes: '30',
    // Carried but not rendered: there is one provider today, and rewriting a
    // stored value to a constant on every unrelated save is how config
    // quietly changes under someone.
    provider: 'GRAVITON_PAY',
  })
  // Cash: the lender's default ceiling, the deposit window, and the accounts
  // the agent picks from. Banks are held as a list rather than a CSV because
  // each row is five fields, and an account number split on a comma is a
  // reconciliation problem nobody would trace back to this screen.
  const [cash, setCash] = useState({
    exposure_ceiling_inr: '50000',
    deposit_within_hours: '24',
    deposit_banks: [] as DepositBank[],
  })
  // '' = not set, so the tier default applies. With nothing stored, the
  // server's effective value is that default, so it doubles as the placeholder.
  const [freshness, setFreshness] = useState({ aging_hours: '', stale_hours: '' })
  // '' = not set, so reports allow the default 31 days.
  const [reportsMaxDays, setReportsMaxDays] = useState('')
  const { data: effectiveFreshness } = useFreshness()
  const [branding, setBranding] = useState({
    logoUrl: '',
    primaryColor: DEFAULT_PRIMARY_COLOR,
  })
  // '' = not set: no default, LMS sync stays off until one is chosen.
  const [lms, setLms] = useState({ mode: '' as '' | 'MOCK' | 'LIVE' })
  const [receiptTerms, setReceiptTerms] = useState('')

  useEffect(() => {
    if (!data) return
    const c = data.conduct ?? {}
    const r = data.reconciliation ?? {}
    setConduct({
      contact_hours_start: c.contact_hours_start ?? '08:00',
      contact_hours_end: c.contact_hours_end ?? '19:30',
      contact_zone: c.contact_zone ?? 'Asia/Kolkata',
      frequency_cap_per_day:
        c.frequency_cap_per_day != null ? String(c.frequency_cap_per_day) : '3',
      contact_working_days: c.contact_working_days ?? DEFAULT_WORKING_DAYS,
    })
    setRecon({
      auto_action_confidence_threshold:
        r.auto_action_confidence_threshold != null
          ? String(r.auto_action_confidence_threshold)
          : '85',
      auto_action_kinds: r.auto_action_kinds ?? 'EXTERNALLY_PAID',
    })
    const s = data.security ?? {}
    setSecurity({
      manager_totp_required: s.manager_totp_required ?? true,
    })
    const t = data.trips?.auto_approve ?? {}
    setTrips({
      auto_approve_enabled: t.enabled ?? false,
      auto_approve_mode: t.mode ?? 'ALL',
      auto_approve_agent_emp_ids: (t.agent_emp_ids ?? []).join(', '),
    })
    const g = data.payments?.gateway ?? {}
    setPayments({
      mode: g.mode === 'MOCK' || g.mode === 'LIVE' ? g.mode : '',
      link_expiry_minutes:
        g.link_expiry_minutes != null ? String(g.link_expiry_minutes) : '30',
      provider: g.provider ?? 'GRAVITON_PAY',
    })
    const cs = data.cash ?? {}
    setCash({
      exposure_ceiling_inr: cs.exposure_ceiling_inr ?? '50000',
      deposit_within_hours:
        cs.deposit_within_hours != null ? String(cs.deposit_within_hours) : '24',
      deposit_banks: (cs.deposit_banks ?? []).map((b) => ({ ...b })),
    })
    const fr = data.freshness ?? {}
    setFreshness({
      aging_hours: fr.aging_hours != null ? String(fr.aging_hours) : '',
      stale_hours: fr.stale_hours != null ? String(fr.stale_hours) : '',
    })
    setReportsMaxDays(data.reports?.max_range_days != null ? String(data.reports.max_range_days) : '')
    const b = data.branding ?? {}
    setBranding({
      logoUrl: b.logoUrl ?? '',
      primaryColor: b.primaryColor ?? DEFAULT_PRIMARY_COLOR,
    })
    const lm = data.lms?.mode
    setLms({ mode: lm === 'MOCK' || lm === 'LIVE' ? lm : '' })
    setReceiptTerms((data.receipt?.terms ?? []).join('\n'))
  }, [data])

  const dirty = useMemo(() => {
    if (!data) return false
    const c = data.conduct ?? {}
    const r = data.reconciliation ?? {}
    return (
      conduct.contact_hours_start !== (c.contact_hours_start ?? '08:00') ||
      conduct.contact_hours_end !== (c.contact_hours_end ?? '19:30') ||
      conduct.contact_zone !== (c.contact_zone ?? 'Asia/Kolkata') ||
      conduct.frequency_cap_per_day !==
        (c.frequency_cap_per_day != null ? String(c.frequency_cap_per_day) : '3') ||
      conduct.contact_working_days !== (c.contact_working_days ?? DEFAULT_WORKING_DAYS) ||
      recon.auto_action_confidence_threshold !==
        (r.auto_action_confidence_threshold != null
          ? String(r.auto_action_confidence_threshold)
          : '85') ||
      recon.auto_action_kinds !== (r.auto_action_kinds ?? 'EXTERNALLY_PAID') ||
      security.manager_totp_required !== ((data.security ?? {}).manager_totp_required ?? true) ||
      trips.auto_approve_enabled !== (data.trips?.auto_approve?.enabled ?? false) ||
      trips.auto_approve_mode !== (data.trips?.auto_approve?.mode ?? 'ALL') ||
      trips.auto_approve_agent_emp_ids !==
        (data.trips?.auto_approve?.agent_emp_ids ?? []).join(', ') ||
      payments.mode !== (data.payments?.gateway?.mode ?? '') ||
      payments.link_expiry_minutes !==
        (data.payments?.gateway?.link_expiry_minutes != null
          ? String(data.payments.gateway.link_expiry_minutes)
          : '30') ||
      cash.exposure_ceiling_inr !== ((data.cash ?? {}).exposure_ceiling_inr ?? '50000') ||
      cash.deposit_within_hours !==
        ((data.cash ?? {}).deposit_within_hours != null
          ? String(data.cash!.deposit_within_hours)
          : '24') ||
      JSON.stringify(cash.deposit_banks) !== JSON.stringify(data.cash?.deposit_banks ?? []) ||
      freshness.aging_hours !==
        (data.freshness?.aging_hours != null ? String(data.freshness.aging_hours) : '') ||
      freshness.stale_hours !==
        (data.freshness?.stale_hours != null ? String(data.freshness.stale_hours) : '') ||
      branding.logoUrl !== ((data.branding ?? {}).logoUrl ?? '') ||
      branding.primaryColor !== ((data.branding ?? {}).primaryColor ?? DEFAULT_PRIMARY_COLOR) ||
      lms.mode !== (data.lms?.mode ?? '') ||
      reportsMaxDays !== (data.reports?.max_range_days != null ? String(data.reports.max_range_days) : '') ||
      JSON.stringify(parseReceiptTerms(receiptTerms)) !== JSON.stringify(data.receipt?.terms ?? [])
    )
  }, [data, conduct, recon, security, trips, payments, cash, freshness, branding, lms, receiptTerms, reportsMaxDays])

  const logoUrlError = isLikelyUrl(branding.logoUrl) ? null : 'Enter a valid http(s) URL.'
  const primaryColorError = HEX_COLOR_RE.test(branding.primaryColor)
    ? null
    : 'Enter a hex color, e.g. #204887.'
  const termLines = parseReceiptTerms(receiptTerms)
  const receiptTermsError =
    termLines.length > MAX_RECEIPT_TERMS
      ? `At most ${MAX_RECEIPT_TERMS} points.`
      : termLines.some((t) => t.length > MAX_RECEIPT_TERM_LENGTH)
        ? `Each point can be at most ${MAX_RECEIPT_TERM_LENGTH} characters.`
        : null
  const brandingValid = !logoUrlError && !primaryColorError && !receiptTermsError

  function updateBank(index: number, patch: Partial<DepositBank>) {
    setCash((c) => ({
      ...c,
      deposit_banks: c.deposit_banks.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    }))
  }

  /**
   * A new row's id is minted once and never shown: agents pick by label, but
   * lots store the id, so renaming an account must not orphan its deposits.
   */
  function addBank() {
    setCash((c) => ({
      ...c,
      deposit_banks: [
        ...c.deposit_banks,
        { id: crypto.randomUUID(), label: '', bank: '', account_no: '', ifsc: '' },
      ],
    }))
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!brandingValid) return
    const patch: TenantSettings = {
      conduct: {
        contact_hours_start: conduct.contact_hours_start,
        contact_hours_end: conduct.contact_hours_end,
        contact_zone: conduct.contact_zone,
        frequency_cap_per_day: Number(conduct.frequency_cap_per_day) || 0,
        contact_working_days: conduct.contact_working_days
          .split(',')
          .map((d) => d.trim().toUpperCase())
          .filter(Boolean)
          .join(','),
      },
      reconciliation: {
        auto_action_confidence_threshold:
          Number(recon.auto_action_confidence_threshold) || 0,
        auto_action_kinds: recon.auto_action_kinds
          .split(',')
          .map((k) => k.trim().toUpperCase())
          .filter(Boolean)
          .join(','),
      },
      security: {
        manager_totp_required: security.manager_totp_required,
      },
      trips: {
        auto_approve: {
          enabled: trips.auto_approve_enabled,
          mode: trips.auto_approve_mode,
          agent_emp_ids: trips.auto_approve_agent_emp_ids
            .split(',')
            .map((e) => e.trim().toUpperCase())
            .filter(Boolean),
        },
      },
      // Sections are replaced wholesale, not deep-merged, so this has to
      // round-trip faithfully — otherwise saving an unrelated card here would
      // silently switch digital collections off.
      payments: {
        gateway:
          payments.mode === ''
            ? {}
            : {
                mode: payments.mode,
                provider: payments.provider,
                link_expiry_minutes: Number(payments.link_expiry_minutes) || 30,
              },
      },
      cash: {
        exposure_ceiling_inr: cash.exposure_ceiling_inr.trim() || '50000',
        deposit_within_hours: Number(cash.deposit_within_hours) || 0,
        // Blank optional fields are dropped rather than stored as empty
        // strings, so an account that never had an IFSC doesn't render one.
        deposit_banks: cash.deposit_banks
          .filter((b) => b.id.trim() && b.label.trim())
          .map((b) => {
            const row: DepositBank = { id: b.id.trim(), label: b.label.trim() }
            if (b.bank?.trim()) row.bank = b.bank.trim()
            if (b.account_no?.trim()) row.account_no = b.account_no.trim()
            if (b.ifsc?.trim()) row.ifsc = b.ifsc.trim().toUpperCase()
            return row
          }),
      },
      // Blank means "use the tier default", so it is left out, not stored as 0.
      freshness: {
        ...(Number(freshness.aging_hours) > 0 && { aging_hours: Number(freshness.aging_hours) }),
        ...(Number(freshness.stale_hours) > 0 && { stale_hours: Number(freshness.stale_hours) }),
      },
      branding: {
        logoUrl: branding.logoUrl.trim() || null,
        primaryColor: branding.primaryColor.trim(),
      },
      // Replaced wholesale like payments: always sent, {} when not set.
      lms: lms.mode === '' ? {} : { mode: lms.mode },
      receipt: { terms: termLines },
      // Blank means "use the default", so it is left out, not stored as 0.
      reports: Number(reportsMaxDays) > 0 ? { max_range_days: Math.round(Number(reportsMaxDays)) } : {},
    }
    save.mutate(patch)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
          Tenant administration
        </p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
          <SettingsIcon className="h-5 w-5" /> Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure the conduct guardrails, digital payments, and
          reconciliation auto-actions. Every change emits a
          TENANT.SETTINGS_UPDATED audit event.
        </p>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load settings</AlertTitle>
          <AlertDescription>
            Sign in as a TENANT_ADMIN — TENANT_CONFIG_MANAGE is required.
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-64 w-full" />}


      {data && (
        <form onSubmit={onSubmit} className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>Conduct (RBI guardrails)</CardTitle>
              <CardDescription>
                Hours, zone, working days, and frequency cap. Defaults match
                the RBI baseline (08:00–19:30 IST, Mon–Sat, 3 contacts/day).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Contact hours — start</Label>
                  <Input
                    type="time"
                    value={conduct.contact_hours_start}
                    onChange={(e) =>
                      setConduct((c) => ({ ...c, contact_hours_start: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Contact hours — end</Label>
                  <Input
                    type="time"
                    value={conduct.contact_hours_end}
                    onChange={(e) =>
                      setConduct((c) => ({ ...c, contact_hours_end: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Time zone</Label>
                  <Input
                    value={conduct.contact_zone}
                    placeholder="Asia/Kolkata"
                    onChange={(e) =>
                      setConduct((c) => ({ ...c, contact_zone: e.target.value }))
                    }
                  />
                  <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                    IANA tz name. Hours are evaluated in this zone.
                  </p>
                </div>
                <div>
                  <Label>Frequency cap (per customer per day)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={conduct.frequency_cap_per_day}
                    onChange={(e) =>
                      setConduct((c) => ({ ...c, frequency_cap_per_day: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div>
                <Label>Working days</Label>
                <Input
                  value={conduct.contact_working_days}
                  onChange={(e) =>
                    setConduct((c) => ({ ...c, contact_working_days: e.target.value }))
                  }
                />
                <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                  CSV of MON, TUE, WED, THU, FRI, SAT, SUN.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reconciliation auto-actions</CardTitle>
              <CardDescription>
                Findings at or above the confidence threshold AND of an
                allow-listed kind are auto-resolved at write time (skip the
                ops queue). Two audit events still land per case.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Confidence threshold (0–100)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={recon.auto_action_confidence_threshold}
                    onChange={(e) =>
                      setRecon((r) => ({
                        ...r,
                        auto_action_confidence_threshold: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Eligible kinds</Label>
                  <Input
                    value={recon.auto_action_kinds}
                    onChange={(e) =>
                      setRecon((r) => ({ ...r, auto_action_kinds: e.target.value }))
                    }
                  />
                  <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                    CSV. e.g. EXTERNALLY_PAID, PARTIAL_RECOVERY.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data freshness</CardTitle>
              <CardDescription>
                The badge on the queue, case and dashboard turns amber, then
                red, this many hours after the newest DPD data. A warning
                only — nothing is blocked. Leave blank for the tier default.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Aging after (hours)</Label>
                  <Input
                    type="number"
                    min={1}
                    placeholder={effectiveFreshness && data?.freshness?.aging_hours == null
                      ? String(effectiveFreshness.agingHours) : undefined}
                    value={freshness.aging_hours}
                    onChange={(e) => setFreshness((f) => ({ ...f, aging_hours: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Stale after (hours)</Label>
                  <Input
                    type="number"
                    min={1}
                    placeholder={effectiveFreshness && data?.freshness?.stale_hours == null
                      ? String(effectiveFreshness.staleHours) : undefined}
                    value={freshness.stale_hours}
                    onChange={(e) => setFreshness((f) => ({ ...f, stale_hours: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reports</CardTitle>
              <CardDescription>
                The longest date range one MIS report may cover, both ends
                counted, on screen and in the downloaded file. Leave blank for
                31 days.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-xs space-y-1.5">
                <Label htmlFor="reports-max-days">Maximum range (days)</Label>
                <Input
                  id="reports-max-days"
                  type="number"
                  min={1}
                  max={366}
                  placeholder="31"
                  value={reportsMaxDays}
                  onChange={(e) => setReportsMaxDays(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Security</CardTitle>
              <CardDescription>
                Tenant-level toggles that override the deploy default. This is
                the master switch for TOTP enforcement — per-user overrides in
                Users &amp; roles only take effect while it's on. Every change is
                in the audit chain.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-card p-3 cursor-pointer hover:bg-navy-50">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-navy-700"
                  checked={security.manager_totp_required}
                  onChange={(e) =>
                    setSecurity((s) => ({ ...s, manager_totp_required: e.target.checked }))
                  }
                />
                <span className="text-sm">
                  <span className="block font-medium text-navy-900">
                    Require TOTP for manager logins
                  </span>
                  <span className="block text-2xs text-muted-foreground">
                    When ON, TENANT_ADMIN / BRANCH_MANAGER / TEAM_LEAD users
                    cannot complete a login without enrolling TOTP first, and
                    Users &amp; roles unlocks a per-user override to require it
                    for other individual accounts too. When OFF, TOTP is not
                    required for anyone on this tenant, and the per-user
                    override is disabled. Managers enrol
                    from <span className="font-mono">/profile/security</span>.
                  </span>
                </span>
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Digital payments</CardTitle>
              <CardDescription>
                Whether agents can raise a UPI link or QR, and against which
                provider.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="pay-mode">Gateway</Label>
                <select
                  id="pay-mode"
                  className="h-9 w-full rounded-md border border-slate-200 bg-card px-3 text-sm"
                  value={payments.mode}
                  onChange={(e) =>
                    setPayments((p) => ({
                      ...p,
                      mode: e.target.value as '' | 'MOCK' | 'LIVE',
                    }))
                  }
                >
                  <option value="">Not configured — digital collections refused</option>
                  <option value="MOCK">Simulator — no real money moves</option>
                  <option value="LIVE">Graviton Pay — live</option>
                </select>
                <p className="text-2xs text-muted-foreground">
                  There is deliberately no default. Guessing the simulator in
                  production would report money that never moved; guessing live
                  in a test environment would charge real customers. Until one
                  is chosen, raising a payment fails with a message naming this
                  setting.
                </p>
              </div>

              {payments.mode === 'MOCK' && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-2xs text-amber-900">
                  <span className="block font-medium">
                    Collections taken in this mode are not real.
                  </span>
                  The simulator issues genuine UPI payloads and settles T+1 like
                  the real provider, so the whole journey — QR, receipt,
                  settlement report, reconciliation — works end to end. No money
                  moves. Never leave this on for a tenant taking real payments.
                </div>
              )}

              {payments.mode === 'LIVE' && (
                <div className="rounded-md border border-slate-200 bg-card p-3 text-2xs text-muted-foreground">
                  <span className="block font-medium text-navy-900">
                    Graviton Pay moves real money.
                  </span>
                  Agents can raise links and QRs, and status is confirmed by
                  polling. Two pieces are not wired up yet: there is no inbound
                  webhook receiver, so a payment is only marked paid on the next
                  poll rather than the instant it clears; and Graviton Pay
                  exposes no settlement API, so settlement reconciliation has
                  nothing to pull and must be done from the bank statement.
                </div>
              )}

              {payments.mode !== '' && (
                <div className="space-y-1.5">
                  <Label htmlFor="pay-expiry">Link / QR expiry (minutes)</Label>
                  <Input
                    id="pay-expiry"
                    type="number"
                    min={1}
                    max={1440}
                    value={payments.link_expiry_minutes}
                    onChange={(e) =>
                      setPayments((p) => ({ ...p, link_expiry_minutes: e.target.value }))
                    }
                  />
                  <p className="text-2xs text-muted-foreground">
                    How long a customer has to pay before the request is closed
                    out. The sweep asks the gateway before expiring anything, so
                    a payment made at the last second is not lost.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Its own permission: the card above only chooses simulator or
              live, this one carries the account the money settles into. */}
          {canManageGateway && <PaymentGatewayCredentialsCard />}

          {graviton && (
            <Card>
              <CardHeader>
                <CardTitle>LMS connection</CardTitle>
                <CardDescription>
                  Where customers, loans and dues are read from. Until a mode
                  is chosen, nothing syncs.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1.5">
                <Label htmlFor="lms-mode">Mode</Label>
                <select
                  id="lms-mode"
                  className="h-9 w-full rounded-md border border-slate-200 bg-card px-3 text-sm"
                  value={lms.mode}
                  onChange={(e) => setLms({ mode: e.target.value as '' | 'MOCK' | 'LIVE' })}
                >
                  <option value="">Not set</option>
                  <option value="MOCK">Mock (demo)</option>
                  <option value="LIVE" disabled>Live</option>
                </select>
                <p className="text-2xs text-muted-foreground">
                  The live LMS client isn't available yet
                </p>
              </CardContent>
            </Card>
          )}

          {/* Keyed on the saved mode, not the unsaved select: the demo
              controls refuse unless the server already says MOCK. */}
          {graviton && data.lms?.mode === 'MOCK' && <MockLmsCard />}

          <Card>
            <CardHeader>
              <CardTitle>Cash handling</CardTitle>
              <CardDescription>
                How much cash an agent may hold, how long they may hold it, and
                which accounts they deposit into.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cash-ceiling">Cash in hand limit (₹)</Label>
                  <Input
                    id="cash-ceiling"
                    inputMode="numeric"
                    value={cash.exposure_ceiling_inr}
                    onChange={(e) =>
                      setCash((c) => ({ ...c, exposure_ceiling_inr: e.target.value }))
                    }
                  />
                  <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                    Applies to every agent. A capture that would cross it is
                    refused; the agent is warned from 80%.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cash-deposit-hours">Deposit within (hours)</Label>
                  <Input
                    id="cash-deposit-hours"
                    type="number"
                    min={0}
                    max={336}
                    value={cash.deposit_within_hours}
                    onChange={(e) =>
                      setCash((c) => ({ ...c, deposit_within_hours: e.target.value }))
                    }
                  />
                  <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                    Counted only across days a branch is open — Sundays and the
                    holiday calendar are skipped. 0 switches the deadline off.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Deposit accounts</Label>
                {cash.deposit_banks.length === 0 && (
                  <p className="rounded-md border border-slate-200 bg-card p-3 text-2xs text-muted-foreground">
                    None yet. Agents will be asked only for a deposit reference,
                    and a lot cannot be matched to a bank statement.
                  </p>
                )}
                {cash.deposit_banks.map((bank, i) => (
                  <div
                    key={i}
                    className="grid gap-2 rounded-md border border-slate-200 bg-card p-3 sm:grid-cols-2"
                  >
                    <Input
                      aria-label="Name the agent sees"
                      placeholder="Name the agent sees, e.g. HDFC — Jaipur collections"
                      value={bank.label}
                      onChange={(e) => updateBank(i, { label: e.target.value })}
                    />
                    <Input
                      aria-label="Bank"
                      placeholder="Bank, e.g. HDFC Bank"
                      value={bank.bank ?? ''}
                      onChange={(e) => updateBank(i, { bank: e.target.value })}
                    />
                    <Input
                      aria-label="Account number"
                      placeholder="Account number"
                      className="font-mono"
                      value={bank.account_no ?? ''}
                      onChange={(e) => updateBank(i, { account_no: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <Input
                        aria-label="IFSC"
                        placeholder="IFSC"
                        className="font-mono"
                        value={bank.ifsc ?? ''}
                        onChange={(e) => updateBank(i, { ifsc: e.target.value })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() =>
                          setCash((c) => ({
                            ...c,
                            deposit_banks: c.deposit_banks.filter((_, j) => j !== i),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addBank}>
                  Add an account
                </Button>
                <p className="text-2xs text-muted-foreground">
                  Removing an account stops agents choosing it. Deposits already
                  made against it keep the name they were made under.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Field visits</CardTitle>
              <CardDescription>
                Who can start visits without a Team Lead approving them.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-card p-3 cursor-pointer hover:bg-navy-50">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-navy-700"
                  checked={trips.auto_approve_enabled}
                  onChange={(e) =>
                    setTrips((t) => ({ ...t, auto_approve_enabled: e.target.checked }))
                  }
                />
                <span className="text-sm">
                  <span className="block font-medium text-navy-900">
                    Auto-approve visit requests
                  </span>
                  <span className="block text-2xs text-muted-foreground">
                    When ON, requests skip the approval inbox and are APPROVED
                    on submit — the agent can start immediately. This removes
                    a control: nobody reviews the planned stops before the
                    agent heads out. Requests already covered by an approved
                    weekly plan auto-approve regardless of this setting.
                  </span>
                </span>
              </label>

              {trips.auto_approve_enabled && (
                <div className="space-y-3 rounded-md border border-slate-200 bg-card p-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="trip-auto-mode">Applies to</Label>
                    <select
                      id="trip-auto-mode"
                      className="h-9 w-full rounded-md border border-slate-200 bg-card px-3 text-sm"
                      value={trips.auto_approve_mode}
                      onChange={(e) =>
                        setTrips((t) => ({
                          ...t,
                          auto_approve_mode: e.target.value as 'ALL' | 'ALLOWLIST',
                        }))
                      }
                    >
                      <option value="ALL">Every agent</option>
                      <option value="ALLOWLIST">Only the agents listed below</option>
                    </select>
                  </div>

                  {trips.auto_approve_mode === 'ALLOWLIST' && (
                    <div className="space-y-1.5">
                      <Label htmlFor="trip-auto-agents">Agent employee IDs</Label>
                      <Input
                        id="trip-auto-agents"
                        placeholder="FLD001, FLD002"
                        value={trips.auto_approve_agent_emp_ids}
                        onChange={(e) =>
                          setTrips((t) => ({
                            ...t,
                            auto_approve_agent_emp_ids: e.target.value,
                          }))
                        }
                      />
                      <p className="text-2xs text-muted-foreground">
                        Comma-separated. An empty list auto-approves nobody —
                        every request keeps going to the manager.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <CardTitle>Branding</CardTitle>
              </div>
              <CardDescription>
                Your logo appears in the app header, on the agent app and on
                the receipts customers receive.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <LogoUpload
                logoUrl={branding.logoUrl}
                disabledReason={dirty ? 'Save or discard your other changes first.' : null}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Or a logo URL</Label>
                  <Input
                    value={branding.logoUrl}
                    placeholder="https://…/logo.png"
                    onChange={(e) =>
                      setBranding((b) => ({ ...b, logoUrl: e.target.value }))
                    }
                  />
                  {logoUrlError ? (
                    <p className="mt-1 text-2xs text-destructive">{logoUrlError}</p>
                  ) : (
                    <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                      Filled in by an upload, or a public https URL. Blank for no logo.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Primary color</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={branding.primaryColor}
                      placeholder={DEFAULT_PRIMARY_COLOR}
                      className="font-mono"
                      onChange={(e) =>
                        setBranding((b) => ({ ...b, primaryColor: e.target.value }))
                      }
                    />
                    <input
                      type="color"
                      aria-label="Pick primary color"
                      className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-slate-200 bg-white p-1"
                      value={
                        HEX_COLOR_RE.test(branding.primaryColor)
                          ? expandHex(branding.primaryColor)
                          : DEFAULT_PRIMARY_COLOR
                      }
                      onChange={(e) =>
                        setBranding((b) => ({ ...b, primaryColor: e.target.value }))
                      }
                    />
                  </div>
                  {primaryColorError && (
                    <p className="mt-1 text-2xs text-destructive">{primaryColorError}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Receipt terms &amp; conditions</CardTitle>
              <CardDescription>
                Printed as numbered points at the foot of every receipt: on the
                agent app, the PDF shared with the customer and here on the web.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Label htmlFor="receipt-terms">One point per line</Label>
              <Textarea
                id="receipt-terms"
                rows={6}
                value={receiptTerms}
                placeholder="All cheques/DD payments are subject to realisation."
                onChange={(e) => setReceiptTerms(e.target.value)}
              />
              {receiptTermsError ? (
                <p className="mt-1 text-2xs text-destructive">{receiptTermsError}</p>
              ) : (
                <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                  Up to {MAX_RECEIPT_TERMS} points. Numbering is added for you. Blank for none.
                </p>
              )}
            </CardContent>
          </Card>

          {save.isSuccess && (
            <Alert variant="positive">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Saved</AlertTitle>
              <AlertDescription>
                Settings updated. Conduct changes take effect on the next case
                attempt; recon thresholds apply to findings written from now on.
              </AlertDescription>
            </Alert>
          )}
          {save.isError && (
            <Alert variant="destructive">
              <AlertTitle>Could not save</AlertTitle>
              <AlertDescription>
                {isAxiosError(save.error) && save.error.response?.status === 403
                  ? 'TENANT_CONFIG_MANAGE is required.'
                  : 'Save failed.'}
              </AlertDescription>
            </Alert>
          )}

          {/* In-page save row (the canonical action area). When the user
              scrolls past this row, the sticky banner below kicks in so
              the Save button is always reachable. */}
          <div className="flex items-center justify-end gap-2">
            <span className="text-2xs uppercase tracking-wider text-muted-foreground">
              {dirty ? 'Unsaved changes' : 'No changes'}
            </span>
            <Button type="submit" disabled={!dirty || !brandingValid || save.isPending}>
              <Save className="h-3.5 w-3.5" />
              {save.isPending ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </form>
      )}
      {/* Sticky bottom banner — surfaces dirty state even when the user
          is scrolled far up reviewing changes. Pairs with the
          beforeunload guard so accidental tab close is caught too. */}
      <UnsavedChangesBar
        dirty={dirty && brandingValid}
        saving={save.isPending}
        onSave={() => {
          const form = document.querySelector<HTMLFormElement>('form')
          form?.requestSubmit()
        }}
      />
    </div>
  )
}

/**
 * Per-lender feature switches. Outside the settings form on purpose: each
 * switch applies the moment it is flipped and is its own audit event, so it
 * never waits behind — or gets bundled into — an unrelated Save.
 */
function UnsavedChangesBar({
  dirty,
  saving,
  onSave,
}: {
  dirty: boolean
  saving: boolean
  onSave: () => void
}) {
  // Tab-close / nav-away guard. Browsers ignore the custom string but
  // honour returnValue; without this the user can lose edits on a fat
  // finger.
  useEffect(() => {
    if (!dirty) return
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  if (!dirty) return null
  return (
    <div className="pointer-events-none sticky bottom-3 z-10 flex justify-center">
      <div className="pointer-events-auto flex items-center gap-3 rounded-md border border-warning/40 bg-warning-soft/95 px-3 py-2 shadow-lift backdrop-blur">
        <span className="text-xs font-medium text-navy-900">
          You have unsaved changes
        </span>
        <Button size="sm" disabled={saving} onClick={onSave}>
          <Save className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save now'}
        </Button>
      </div>
    </div>
  )
}

/**
 * Pick a PNG or JPEG and upload it; the server stores it and sets the logo
 * URL. Kept out of the form's Save: the file goes up on its own, and the
 * page then reloads its settings, which would discard unsaved edits — hence
 * disabled while the form is dirty.
 */
function LogoUpload({ logoUrl, disabledReason }: { logoUrl: string; disabledReason: string | null }) {
  const upload = useUploadLogo()
  const src = resolveLogoUrl(logoUrl.trim() || null)
  const error = upload.error
    ? isAxiosError(upload.error)
      ? ((upload.error.response?.data as { message?: string } | undefined)?.message ?? 'Upload failed.')
      : 'Upload failed.'
    : null

  return (
    <div className="flex items-center gap-4">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white">
        {src ? (
          <img src={src} alt="Current logo" className="h-14 w-14 object-contain" />
        ) : (
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">No logo</span>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor="logo-file">Upload logo</Label>
        <input
          id="logo-file"
          type="file"
          accept="image/png,image/jpeg"
          disabled={upload.isPending || disabledReason != null}
          className="block text-xs file:mr-3 file:rounded-md file:border file:border-slate-200 file:bg-white file:px-3 file:py-1.5 file:text-xs"
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (file) upload.mutate(file)
          }}
        />
        {disabledReason ? (
          <p className="text-2xs text-muted-foreground">{disabledReason}</p>
        ) : upload.isPending ? (
          <p className="text-2xs text-muted-foreground">Uploading…</p>
        ) : error ? (
          <p className="text-2xs text-destructive">{error}</p>
        ) : upload.isSuccess ? (
          <p className="text-2xs text-positive">Logo updated.</p>
        ) : (
          <p className="text-2xs uppercase tracking-wider text-muted-foreground">PNG or JPEG, up to 512 KB</p>
        )}
      </div>
    </div>
  )
}
